import { SVGAttributes } from 'react';

import { iconRegistry, type IconName } from './registry';

export type IconVariant =
  | 'linear'
  | 'bold'
  | 'outline'
  | 'broken'
  | 'bulk'
  | 'twotone';

export interface IconProps
  extends Omit<SVGAttributes<SVGElement>, 'name' | 'color'> {
  name: IconName;
  variant?: IconVariant;
  size?: number | string;
  color?: string;
}

const variantToIconsax: Record<
  IconVariant,
  'Linear' | 'Bold' | 'Outline' | 'Broken' | 'Bulk' | 'TwoTone'
> = {
  linear: 'Linear',
  bold: 'Bold',
  outline: 'Outline',
  broken: 'Broken',
  bulk: 'Bulk',
  twotone: 'TwoTone',
};

/**
 * Magpiie's single icon component. Backed by iconsax-react with a few custom
 * Magpiie-specific icons. Names follow the design system catalog
 * (magpiie-ui-ux/skills/magpiie-icon.md).
 *
 * Defaults: 20px linear stroke icons that inherit `currentColor`.
 *
 * Sizes by context (DS convention):
 *   - 16px inline (form fields, small buttons)
 *   - 20px default
 *   - 24px nav / action buttons
 *   - 32px empty states
 */
export const Icon = ({
  name,
  variant = 'linear',
  size = 20,
  color = 'currentColor',
  ...rest
}: IconProps) => {
  const Component = iconRegistry[name] ?? iconRegistry.home;
  return (
    <Component
      size={size}
      color={color}
      variant={variantToIconsax[variant]}
      {...rest}
    />
  );
};

export type { IconName };
